<template>
  <div class="patient-banner" v-if="patient">
    <!-- Header: Identity (Left) & Vitals (Center) -->
    <div class="banner-header">
      <!-- Left: Identity -->
      <div class="identity-group">
        <h2 class="patient-name">
          {{ patient.personal_information?.first_name }}
          <span class="last-name">{{
            patient.personal_information?.last_name
          }}</span>
        </h2>
        <span class="status-tag" :class="getStatusClass(patient.status)">{{
          patient.status
        }}</span>
        <!-- <span v-if="patient.personal_information?.similar_sounding_name" style="color: var(--red-500, #EF4444); font-weight: bold; margin-left: 15px; display: inline-flex; align-items: center; gap: 6px; font-size: 1.1em; background-color: rgba(239, 68, 68, 0.1); padding: 4px 10px; border-radius: 6px;">
                    <i class="pi pi-exclamation-triangle"></i> Similar Name Detected
                </span> -->
        <div
          v-if="patient.personal_information?.similar_sounding_name"
          class="similar-name-tag-patient-general"
        >
          ⚠️ Similar Sounding Name
        </div>
      </div>

      <!-- Center: Core Vitals -->
      <div class="vitals-group">
        <div
          class="vital-item"
          v-if="patient.personal_information?.preferred_name"
        >
          <span class="label">Pref. Name</span>
          <span class="value">{{
            patient.personal_information?.preferred_name
          }}</span>
        </div>
        <div class="vital-item">
          <span class="label">DOB (Age)</span>
          <span class="value"
            >{{ patient.personal_information?.dob }} ({{
              patient.personal_information?.age
            }})</span
          >
        </div>
        <div class="vital-item">
          <span class="label">Sex</span>
          <span class="value">{{ patient.personal_information?.sex }}</span>
        </div>
        <div class="vital-item">
          <span class="label">Height / Weight</span>
          <span class="value">
            {{
              patient.latest_height_reading?.value
                ? patient.latest_height_reading.value + "cm"
                : "-"
            }}
            /
            {{
              patient.latest_weight_reading?.value
                ? patient.latest_weight_reading.value + "kg"
                : "-"
            }}
          </span>
        </div>
      </div>

      <!-- Right: Allergies -->
      <div
        class="vital-item allergy-vital-item"
        style="cursor: pointer"
        @click="displayADRModal = true"
      >
        <span class="label">Allergies (ADR)</span>
        <span v-if="!hasAllergies && !hasNkda" class="value">-</span>
        <span
          v-else-if="!hasAllergies && hasNkda"
          class="value allergy-nkda"
          >No Known Drug Allergies
        </span>
        <span v-else class="value allergy-inline">
          <template v-for="(entry, index) in allergyNames" :key="index"
            ><span class="allergy-name" :title="entry.full">{{
              entry.short
            }}</span
            ><span v-if="index < allergyNames.length - 1"> | </span
          ></template>
        </span>
      </div>
    </div>

    <!-- Secondary Info Grid (IDs & Contact) -->
    <div class="banner-secondary-wrapper">
      <div class="banner-data-grid">
        <div class="data-item">
          <span class="label">{{ $resident({ capitalize: true }) }} ID</span>
          <span class="value">{{ patient.uuid }}</span>
        </div>
        <div class="data-item">
          <span class="label">Concession No.</span>
          <span class="value">{{ concession ? concession.data : "-" }}</span>
        </div>
        <div class="data-item">
          <span class="label">Medicare</span>
          <span class="value">{{ medicare ? medicare.data : "-" }}</span>
        </div>
        <div class="data-item">
          <span class="label">DVA</span>
          <span class="value">
            {{ dva ? dva.data : "-" }}
            <span
              v-if="
                dva &&
                dva.regulatory_type &&
                dva.regulatory_type !== 'DVA Number'
              "
              style="font-size: 0.85em; margin-left: 5px; font-weight: normal"
            >
              ({{ dva.regulatory_type.replace("DVA ", "") }})
            </span>
          </span>
        </div>
        <div class="data-item">
          <span class="label">IHI Number</span>
          <span class="value">
            {{ ihi ? ihi.number : "-" }}
          </span>
        </div>
        <div
          class="data-item"
          style="grid-column: span 2; max-width: 100%; min-width: 0"
        >
          <span class="label">Pharmacy</span>
          <div
            class="value"
            style="
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              text-align: left;
              width: 100%;
            "
          >
            <template v-if="patient.pharmacy">
              <span
                v-if="patient.pharmacy.name"
                :title="patient.pharmacy.name"
                >{{ patient.pharmacy.name }}</span
              >
              <span
                v-if="formattedPharmacyAddress !== '-'"
                :title="formattedPharmacyAddress"
                >{{ formattedPharmacyAddress }}</span
              >
              <span
                v-if="formattedPharmacyContacts !== '-'"
                :title="formattedPharmacyContacts"
                >{{ formattedPharmacyContacts }}</span
              >
            </template>
            <template v-else>-</template>
          </div>
        </div>

        <div class="data-item">
          <span class="label">Psychotropic Consent</span>
          <span
            class="value white-text"
            :class="
              psychotropicConsent && psychotropicConsent.has_consented
                ? 'status-active'
                : 'status-inactive'
            "
          >
            {{
              psychotropicConsent && psychotropicConsent.has_consented
                ? "Yes"
                : "No"
            }}
            <span
              v-if="psychotropicConsent && psychotropicConsent.given_by"
              style="font-size: 0.85em; font-weight: normal"
            >
              - {{ psychotropicConsent.given_by }}</span
            >
            <span
              v-if="psychotropicConsent && psychotropicConsent.given_date"
              style="font-size: 0.85em; font-weight: normal"
            >
              ({{ psychotropicConsent.given_date }})</span
            >
          </span>
        </div>

        <div class="data-item">
          <span class="label">Prescribing Consent</span>
          <span
            class="value white-text"
            :class="
              prescribingConsent && prescribingConsent.has_consented
                ? 'status-active'
                : 'status-inactive'
            "
          >
            {{
              prescribingConsent && prescribingConsent.has_consented
                ? "Yes"
                : "No"
            }}
            <span
              v-if="prescribingConsent && prescribingConsent.given_by"
              style="font-size: 0.85em; font-weight: normal"
            >
              - {{ prescribingConsent.given_by }}</span
            >
            <span
              v-if="prescribingConsent && prescribingConsent.given_date"
              style="font-size: 0.85em; font-weight: normal"
            >
              ({{ prescribingConsent.given_date }})</span
            >
          </span>
        </div>

        <!-- Full Width Address -->
        <div class="data-item">
          <span class="label">Address</span>
          <span class="value">{{ formattedAddress }}</span>
        </div>
      </div>
    </div>

    <ADRModal
      v-if="displayADRModal"
      :patient="patient"
      @close="displayADRModal = false"
    />
  </div>
</template>

<script>
import ADRModal from "@/components/modals/ADRModal.vue";
import { getDrugDisplayName } from "@/services/drugNames";
import { getRealAdrs, hasNkdaAssertion } from "@/services/nkda";

export default {
  props: ["patient", "ihi", "medicare", "dva", "concession"],
  components: {
    ADRModal,
  },
  data() {
    return {
      displayADRModal: false,
    };
  },
  computed: {
    formattedAddress() {
      if (this.patient.address) {
        const { street_address, suburb, state, postcode } =
          this.patient.address;
        return `${street_address}, ${suburb}, ${state} ${postcode}`;
      }
      return "-";
    },
    formattedPharmacyAddress() {
      if (this.patient.pharmacy && this.patient.pharmacy.address) {
        const { street_address, suburb, state, postcode } =
          this.patient.pharmacy.address;
        const parts = [];
        if (street_address) parts.push(street_address);
        if (suburb) parts.push(suburb);
        if (state || postcode)
          parts.push(`${state || ""} ${postcode || ""}`.trim());
        return parts.length > 0 ? parts.join(", ") : "-";
      }
      return "-";
    },
    formattedPharmacyContacts() {
      if (!this.patient.pharmacy) return "-";

      let contacts = [];

      if (this.patient.pharmacy.email && this.patient.pharmacy.email.data) {
        contacts.push(`${this.patient.pharmacy.email.data}`);
      }
      if (this.patient.pharmacy.phone && this.patient.pharmacy.phone.data) {
        contacts.push(`${this.patient.pharmacy.phone.data}`);
      }
      if (this.patient.pharmacy.fax && this.patient.pharmacy.fax.data) {
        contacts.push(`Fax: ${this.patient.pharmacy.fax.data}`);
      }

      if (
        contacts.length === 0 &&
        this.patient.pharmacy.contact_methods &&
        this.patient.pharmacy.contact_methods.length > 0
      ) {
        return this.patient.pharmacy.contact_methods
          .map((c) => {
            return `${c.data}`;
          })
          .join(" | ");
      }

      return contacts.length > 0 ? contacts.join(" | ") : "-";
    },
    psychotropicConsent() {
      return this.patient.psychotropic_consent || null;
    },
    prescribingConsent() {
      return this.patient.prescribing_consent || null;
    },
    realAdrs() {
      return getRealAdrs(this.patient.adr);
    },
    hasAllergies() {
      return this.realAdrs.length > 0;
    },
    hasNkda() {
      return hasNkdaAssertion(this.patient.adr);
    },
    allergyNames() {
      if (!this.hasAllergies) return [];
      return this.realAdrs.map((adr) => getDrugDisplayName(adr));
    },
  },
  methods: {
    getStatusClass(status) {
      if (!status) return "";
      switch (status.toLowerCase()) {
        case "active":
          return "status-active";
        case "inactive":
          return "status-inactive";
        case "onboarding":
          return "status-onboarding";
        default:
          return "";
      }
    },
  },
};
</script>

<style scoped>
.patient-banner {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  padding: 12px 16px;
  margin-bottom: 20px;
  font-family: "Inter", system-ui, sans-serif;
  color: #344767;
  border: 1px solid #e0e0e0;
}

/* Header Layout using Grid for Centering */
.banner-header {
  display: grid;
  grid-template-columns: 1fr auto 1fr; /* Center is auto, sides are equal */
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
  gap: 15px;
}

/* Left: Identity */
.identity-group {
  display: flex;
  align-items: center;
  gap: 12px;
  justify-self: start;
}

.patient-name {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 500;
  color: #0870dc;
  line-height: 1.2;
}

.last-name {
  font-weight: 700;
}

/* Center: Vitals */
.vitals-group {
  display: flex;
  align-items: center;
  gap: 24px; /* Broader gap for cleaner separation without lines */
  justify-self: center;
}

.vital-item {
  display: flex;
  flex-direction: column;
  align-items: center; /* Center text in vital items */
  text-align: center;
}

/* Right Spacer */
.header-right-spacer {
  justify-self: end;
}

/* Common Text Styles */
.label {
  font-size: 1rem;
  text-transform: uppercase;
  color: #6a7381;
  font-weight: 600;
  margin-bottom: 3px;
  white-space: nowrap;
  letter-spacing: 0.5px;
}

.value {
  font-size: 1.1rem;
  font-weight: 600;
  color: #2b3648;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.white-text {
  color: white;
}

.allergy-vital-item {
  align-items: flex-start;
  text-align: left;
  max-width: 100%;
  min-width: 0; /* let this grid/flex item shrink below its content width so the inline list scrolls instead of stretching the layout */
}

.allergy-inline {
  display: block;
  color: #e04f39;
  font-weight: 700;
  white-space: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  text-overflow: initial;
  max-width: 100%;
  text-align: left;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* legacy Edge/IE */
}

.allergy-inline::-webkit-scrollbar {
  display: none; /* Chrome/Safari - still scrollable via wheel/trackpad/drag, just no visible bar */
}

.allergy-name {
  display: inline-block;
  white-space: nowrap;
  vertical-align: bottom;
}

.allergy-nkda {
  color: #00ad50;
  font-weight: 700;
}

/* Status Tags */
.status-tag {
  padding: 2px 6px;
  border-radius: 4px;
  color: white;
  font-weight: 700;
  font-size: 0.65rem;
  text-transform: uppercase;
}
.status-active {
  background-color: #00ad50;
  padding: 5px;
  border-radius: 6px;
}
.status-inactive {
  background-color: #e04f39;
  padding: 5px;
  border-radius: 6px;
}
.status-onboarding {
  background-color: #ef9600;
  padding: 5px;
  border-radius: 6px;
}

/* Secondary Info Grid + Allergies Sidebar */
.banner-secondary-wrapper {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

.banner-data-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  column-gap: 15px;
  row-gap: 12px;
  align-items: flex-start;
  flex: 1;
  min-width: 0;
}

.data-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start; /* Ensure left alignment for grid items */
}

/* Patient ID specific style to handle long UUIDs */
.data-item .value {
  font-family: "Inter", system-ui, sans-serif; /* Keep same font */
  font-size: 1rem; /* Slightly smaller for potential long IDs */
}

.full-width-address {
  grid-column: 2 / -1;
}

.link-color {
  color: #0870dc;
}

/* Responsiveness */
@media (max-width: 1200px) {
  .banner-data-grid {
    grid-template-columns: repeat(4, 1fr);
  }
  .full-width-address {
    grid-column: 1 / -1;
    margin-top: 4px;
  }
}

@media (max-width: 900px) {
  .banner-header {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }
  .vitals-group {
    width: 100%;
    justify-content: flex-start;
    gap: 15px;
  }
  .vital-item {
    align-items: flex-start;
    text-align: left;
  }
  .header-right-spacer {
    display: none;
  }
  .banner-secondary-wrapper {
    flex-direction: column;
  }
}

@media (max-width: 600px) {
  .vitals-group {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }

  .banner-data-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
